function normalizeAltText(value) {
  return typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '';
}

function imageHasAltText(image) {
  return image.hasAttribute('alt') && normalizeAltText(image.getAttribute('alt')) !== '';
}

function altTextFromFilename(src) {
  if (!src) return '';

  try {
    const pathname = new URL(src, window.location.origin).pathname;
    const filename = pathname.split('/').pop()?.replace(/\.[^.]+$/, '') || '';

    return normalizeAltText(filename.replace(/[-_]+/g, ' '));
  } catch (error) {
    return '';
  }
}

function getPrecedingHeadingText(image, boundary) {
  let node = image;

  while (node && node !== boundary) {
    let previous = node.previousElementSibling;

    while (previous) {
      if (/^H[1-6]$/i.test(previous.tagName)) {
        const headingText = normalizeAltText(previous.textContent);
        if (headingText) return headingText;
      }

      const nestedHeading = previous.querySelector?.('h1, h2, h3, h4, h5, h6');
      const nestedHeadingText = normalizeAltText(nestedHeading?.textContent);
      if (nestedHeadingText) return nestedHeadingText;

      previous = previous.previousElementSibling;
    }

    node = node.parentElement;
  }

  return '';
}

function getProductDetailsImageAlt(image, body) {
  const figure = image.closest('figure');
  const figcaptionText = normalizeAltText(figure?.querySelector('figcaption')?.textContent);
  if (figcaptionText) return figcaptionText;

  const titleText = normalizeAltText(image.getAttribute('title'));
  if (titleText) return titleText;

  const bodyHeadingText = getPrecedingHeadingText(image, body);
  if (bodyHeadingText) return bodyHeadingText;

  const block = body.closest('.bst-product-details__block');
  const subheadingText = normalizeAltText(block?.querySelector('.bst-product-details__subheading')?.textContent);
  if (subheadingText) return subheadingText;

  const container = body.closest('.bst-product-details__column, .bst-product-details__panel, .bst-product-details');
  const sectionHeadingText = normalizeAltText(
    container?.querySelector('.bst-product-details__heading')?.textContent
  );
  if (sectionHeadingText) return sectionHeadingText;

  const section = body.closest('.bst-product-details');
  const productTitle = normalizeAltText(section?.dataset.productTitle);
  if (productTitle) return productTitle;

  const pageProductTitle = normalizeAltText(document.querySelector('.product__title h1')?.textContent);
  if (pageProductTitle) return pageProductTitle;

  return altTextFromFilename(image.currentSrc || image.getAttribute('src'));
}

function getHeadingLevel(element) {
  const match = element.tagName.match(/^H(\d)$/i);
  return match ? Number(match[1]) : null;
}

function replaceHeadingElement(heading, level) {
  const tagName = `h${level}`;
  if (heading.tagName.toLowerCase() === tagName) return heading;

  const replacement = document.createElement(tagName);

  replacement.innerHTML = heading.innerHTML;

  for (const { name, value } of heading.attributes) {
    replacement.setAttribute(name, value);
  }

  heading.replaceWith(replacement);
  return replacement;
}

function getProductDetailsBodyHeadingBase(body) {
  if (body.closest('.bst-product-details__block')?.querySelector('.bst-product-details__subheading')) {
    return 4;
  }

  return 3;
}

function normalizeProductDetailsBodyHeadings(body) {
  if (body.dataset.bstHeadingsNormalized === 'true') return;

  const headings = Array.from(body.querySelectorAll('h1, h2, h3, h4, h5, h6'));
  if (!headings.length) {
    body.dataset.bstHeadingsNormalized = 'true';
    return;
  }

  const baseLevel = getProductDetailsBodyHeadingBase(body);
  let previousLevel = baseLevel - 1;

  headings.forEach((heading, index) => {
    const currentLevel = getHeadingLevel(heading);
    if (!currentLevel) return;

    let targetLevel;

    if (index === 0) {
      targetLevel = baseLevel;
    } else if (currentLevel > previousLevel + 1) {
      targetLevel = previousLevel + 1;
    } else if (currentLevel <= previousLevel) {
      targetLevel = Math.max(baseLevel, previousLevel - (previousLevel - currentLevel));
    } else {
      targetLevel = currentLevel;
    }

    targetLevel = Math.min(6, Math.max(baseLevel, targetLevel));
    const normalizedHeading = replaceHeadingElement(heading, targetLevel);
    previousLevel = getHeadingLevel(normalizedHeading) || targetLevel;
  });

  body.dataset.bstHeadingsNormalized = 'true';
}

function ensureBstProductDetailsImageAlts(scope = document) {
  scope.querySelectorAll('.bst-product-details__body').forEach((body) => {
    normalizeProductDetailsBodyHeadings(body);

    body.querySelectorAll('img').forEach((image) => {
      if (imageHasAltText(image)) return;

      const altText = getProductDetailsImageAlt(image, body);
      if (!altText) return;

      image.setAttribute('alt', altText);
    });
  });
}

function initBstProductDetails(scope = document) {
  ensureBstProductDetailsImageAlts(scope);
}

window.ensureBstProductDetailsImageAlts = ensureBstProductDetailsImageAlts;
window.initBstProductDetails = initBstProductDetails;

document.addEventListener('DOMContentLoaded', () => initBstProductDetails());
document.addEventListener('shopify:section:load', (event) => initBstProductDetails(event.target));

if (!customElements.get('bst-product-details-tabs')) {
  customElements.define(
    'bst-product-details-tabs',
    class BstProductDetailsTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = [];
        this.panels = [];
        this.tabsNav = null;
        this.headerResizeObserver = null;
        this.onTabClick = this.onTabClick.bind(this);
        this.onResize = this.onResize.bind(this);
      }

      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[data-bst-product-details-tab]'));
        this.panels = Array.from(this.querySelectorAll('[data-bst-product-details-panel]'));
        this.tabsNav = this.querySelector('.bst-product-details__tabs-nav');

        if (!this.tabs.length || !this.panels.length) return;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
        });

        if (this.tabsNav) {
          this.observeHeaderHeight();
          this.updateStickyOffset();

          window.addEventListener('resize', this.onResize, { passive: true });

          requestAnimationFrame(() => this.updateStickyOffset());
        }

        const initialTab = this.tabs.find((tab) => tab.classList.contains('is-active')) || this.tabs[0];
        this.activate(initialTab.dataset.bstProductDetailsTab, false);
        ensureBstProductDetailsImageAlts(this);
      }

      disconnectedCallback() {
        this.tabs.forEach((tab) => {
          tab.removeEventListener('click', this.onTabClick);
        });
        window.removeEventListener('resize', this.onResize);
        this.headerResizeObserver?.disconnect();
        this.headerResizeObserver = null;
      }

      onResize() {
        this.updateStickyOffset();
      }

      observeHeaderHeight() {
        this.headerResizeObserver?.disconnect();

        const header = document.querySelector('.bst-header');
        if (!header) return;

        this.headerResizeObserver = new ResizeObserver(() => {
          this.updateStickyOffset();
        });

        this.headerResizeObserver.observe(header);
      }

      updateStickyOffset() {
        if (!this.tabsNav) return;

        const offsetValue = `${this.getHeaderOffset()}px`;
        document.documentElement.style.setProperty('--bst-product-details-tabs-sticky-top', offsetValue);
        this.tabsNav.style.setProperty('--bst-product-details-tabs-sticky-top', offsetValue);
      }

      getHeaderOffset() {
        const header = document.querySelector('.bst-header');
        if (header) {
          const height = Math.ceil(header.getBoundingClientRect().height);
          if (height > 0) return height;
        }

        return window.matchMedia('(min-width: 990px)').matches ? 72 : 50;
      }

      onTabClick(event) {
        const tab = event.currentTarget;
        this.activate(tab.dataset.bstProductDetailsTab, true);
      }

      activate(tabId, shouldFocus) {
        if (!tabId) return;

        this.tabs.forEach((tab) => {
          const isActive = tab.dataset.bstProductDetailsTab === tabId;
          tab.classList.toggle('is-active', isActive);
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.tabIndex = isActive ? 0 : -1;
        });

        this.panels.forEach((panel) => {
          const isActive = panel.dataset.bstProductDetailsPanel === tabId;
          panel.classList.toggle('is-active', isActive);
          panel.hidden = !isActive;
        });

        if (shouldFocus) {
          const activeTab = this.tabs.find((tab) => tab.dataset.bstProductDetailsTab === tabId);
          activeTab?.focus();
        }

        ensureBstProductDetailsImageAlts(this);
      }
    }
  );
}
